import { apiStatus, platform } from '@va/constants';
import { getAccountInformation, getApiRequest as getCoreApiRequest } from '@va/dashboard/selectors/api';
import {
  activeWebsiteSelectors,
  getWebsite,
  selectHasPermission,
  websiteRoleSelectors,
} from '@va/dashboard/selectors/core';
import dayjs from 'dayjs';

import { accountType, FREE_PLAN_DETAILS, subscriptionStatus, subscriptionType } from '@va/standalone/shared/constants';
import { WixPermissionPack } from '@va/wix/shared/constants';
import Config from 'Config';
import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { getApiRequest, getApiRequestStatus } from './api';

export const initializing = (state) => state.getIn(['app', 'initializing'], undefined);

/**
 * @deprecated
 * Use {@link websiteRoleSelectors.isWebsiteContributor} from `@va/dashboard/selectors/core` instead.
 */
export const contributorRole = (state) => {
  return websiteRoleSelectors.isWebsiteContributor(state);
};

/**
 * @deprecated
 * Use {@link websiteRoleSelectors.hasUserRole} from `@va/dashboard/selectors/core` instead.
 */
export const hasUserRole = (state) => {
  return websiteRoleSelectors.hasUserRole(state);
};

/**
 * @deprecated
 * Use {@link websiteRoleSelectors.isWebsiteOwner} from `@va/dashboard/selectors/core` instead.
 */
export const isWebsiteOwner = (state) => {
  return websiteRoleSelectors.isWebsiteOwner(state);
};

/**
 * @deprecated
 * Use {@link websiteRoleSelectors.selectWebsiteRole} from `@va/dashboard/selectors/core` instead.
 */
export const getUserRole = (state) => {
  return websiteRoleSelectors.selectWebsiteRole(state);
};

export const isActiveWebsiteWhiteLabeled = createSelector([getWebsite], (website) => website?.whiteLabel);

/**
 * @deprecated
 * Use {@link selectHasPermission} from `@va/dashboard/selectors/core` instead.
 */
export const hasPermission = (state, action, subFeature, tabName) => {
  return selectHasPermission(state, action, subFeature, tabName);
};

/**
 * @deprecated
 * Use {@link activeWebsiteSelectors.selectIsLoading} from `@va/dashboard/selectors/core` instead
 */
export const isSettingActiveWebsiteInProgress = (state) => activeWebsiteSelectors.selectIsLoading(state);

export const isSumoUser = (state) => {
  const accountInformation = getAccountInformation(state);

  return accountInformation.type === accountType.SUMO;
};

export const hasAppliedLifeTimeDeal = (state) => {
  const website = getWebsite(state);
  return website.sumoActive;
};

export const getAppsumoStep = (state) => state.getIn(['app', 'appsumoStep'], null);
export const getAppsumoUserId = (state) => state.getIn(['app', 'appsumoUserId'], null);
export const getVouchersList = (state) => state.getIn(['app', 'listOfVouchers'], Immutable.Map());

export const isBrainTreeInstanceLoaded = (state) => state.getIn(['app', 'brainTreeInstanceLoaded'], false);

/** @deprecated */
export const hasAllRequiredCustomerDetails = (state) => {
  const customer = getApiRequest(state, 'getCustomer');
  return (
    customer.get('id', null) !== null &&
    customer.get('companyName', null) !== null &&
    customer.get('country', null) !== null &&
    customer.get('firstName', null) !== null &&
    customer.get('lastName', null) !== null &&
    customer.get('address', null) !== null
  );
};

export const hasFreePlan = (state) => {
  const featurePermissions = getCoreApiRequest(state, 'getFeaturePermissions');
  return featurePermissions && featurePermissions.visits && featurePermissions.visits.limit <= FREE_PLAN_DETAILS.visits;
  // const website = getWebsite(state);
  // return !website.isPayable && !website.unlimitedPlanActive && !website.hasAppliedLifeTimeDeal;
};

export const getPaymentPlans = (state) => {
  const paymentPlansStatus = getApiRequestStatus(state, 'getPaymentPlans');
  const paymentPlans = getApiRequest(state, 'getPaymentPlans');
  if (paymentPlansStatus !== apiStatus.SUCCEEDED) {
    return null;
  }

  return {
    monthly: getProcessPaymentPlansByType(paymentPlans, subscriptionType.MONTHLY),
    yearly: getProcessPaymentPlansByType(paymentPlans, subscriptionType.YEARLY),
  };
};

export const getPlanDetailsById = (state, planId) => {
  const paymentPlans = getPaymentPlans(state);
  if (!paymentPlans) return;
  const allPlans = paymentPlans.monthly.concat(paymentPlans.yearly);
  return allPlans.find((plan) => {
    return plan.allPlanIds.includes(String(planId));
  });
};

export const getPlanFrequencyById = (state, planId) => {
  const paymentPlans = getPaymentPlans(state);
  if (!paymentPlans) return;
  for (const [frequency, plans] of Object.entries(paymentPlans)) {
    for (const plan of plans) {
      if (plan.allPlanIds?.includes(planId)) return frequency;
    }
  }
};

const getProcessPaymentPlansByType = (paymentPlans, subscriptionType) => {
  let paymentPlansArrSorted = [
    {
      planId: 'FREE',
      index: -1,
      name: FREE_PLAN_DETAILS.planName,
      visits: FREE_PLAN_DETAILS.visits,
      recordings: FREE_PLAN_DETAILS.recordings,
      heatmaps: FREE_PLAN_DETAILS.heatmaps,
      polls: FREE_PLAN_DETAILS.polls,
      surveys: FREE_PLAN_DETAILS.surveys,
      apiKeys: FREE_PLAN_DETAILS.apiKeys,
      oldPlanIds: [],
      allPlanIds: ['FREE'],
      price: 0,
    },
  ];
  const badgeColors = ['#EAA03D', '#C8D000', '#8F7CED', '#54A4DA'];
  paymentPlans
    .get(subscriptionType, Immutable.Map())
    .filter((item) => item.get('active') === true)
    .sortBy((item) => item.get('weight'))
    .forEach((plan, index) => {
      const badgeColorIndex = Math.floor(plan.get('weight') / 100);
      const planId = plan.get('id');
      const oldPlanIds =
        plan
          .get('oldPlanIds')
          ?.toJS()
          ?.map((planId) => String(planId)) ?? [];

      paymentPlansArrSorted.push({
        planId: planId,
        index: Number(index),
        name: plan.get('name'),
        oldPlanIds: oldPlanIds,
        allPlanIds: [...oldPlanIds, planId],
        visits: plan
          .get('permissions')
          .find((item) => item.get('name') === 'VISITS_LIMIT')
          ?.get('value'),
        recordings: plan
          .get('permissions')
          .find((item) => item.get('name') === 'SSR_LIMIT')
          ?.get('value'),
        heatmaps: plan
          .get('permissions')
          .find((item) => item.get('name') === 'HEATMAPS_LIMIT')
          ?.get('value'),
        funnels: plan
          .get('permissions')
          .find((item) => item.get('name') === 'FUNNELS_LIMIT')
          ?.get('value'),
        polls: plan
          .get('permissions')
          .find((item) => item.get('name') === 'POLLS_LIMIT')
          ?.get('value'),
        surveys: plan
          .get('permissions')
          .find((item) => item.get('name') === 'SURVEYS_LIMIT')
          ?.get('value'),
        eventTracking: plan
          .get('permissions')
          .find((item) => item.get('name') === 'EVENT_TRACKING_LIMIT')
          ?.get('value'),
        price: plan.get('price'),
        badge: {
          color: badgeColors[badgeColorIndex - 1],
        },
      });
    });

  return paymentPlansArrSorted;
};

export const getPlanIndex = (state) => {
  const paymentPlans = getPaymentPlans(state);

  const lastCreatedSubscription = getLastCreatedSubscription(state);

  if (!lastCreatedSubscription || !paymentPlans) return -1;

  let monthlyPlanFoundIndex = paymentPlans.monthly.findIndex((plan) => {
    if (
      String(plan.planId) === String(lastCreatedSubscription.planId) ||
      plan.oldPlanIds?.includes(String(lastCreatedSubscription.planId))
    )
      return true;
    return false;
  });

  let yearlyPlanFoundIndex = paymentPlans.yearly.findIndex((plan) => {
    if (
      String(plan.planId) === String(lastCreatedSubscription.planId) ||
      plan.oldPlanIds?.includes(String(lastCreatedSubscription.planId))
    )
      return true;
    return false;
  });

  return Math.max(monthlyPlanFoundIndex, yearlyPlanFoundIndex, 1);
};

/**
 *
 * @deprecated outdated logic
 * TODO Delete
 */
export const getNextBillingPlan = (state) => {
  const subscriptionsResponse = getApiRequest(state, 'getAllSubscriptions');
  const allSubscriptions = subscriptionsResponse.get('data', Immutable.List());
  return (
    allSubscriptions.size > 0 && allSubscriptions.toJS().filter((item) => item.status === subscriptionStatus.PENDING)[0]
  );
};

/**
 *
 * @deprecated outdated logic
 * TODO Delete
 */
export const hasNextBillingPlan = (state) =>
  getNextBillingPlan(state) && getNextBillingPlan(state).id !== null && getNextBillingPlan(state).id !== undefined;

export const getActiveSubscription = (state) => {
  const subscriptionsResponse = getApiRequest(state, 'getAllSubscriptions');
  const allSubscriptions = subscriptionsResponse.get('data', Immutable.List());
  const activeSubscription =
    allSubscriptions.size > 0 && allSubscriptions.toJS().filter((item) => item.status === subscriptionStatus.ACTIVE);
  return activeSubscription && activeSubscription[0];
};

export const getLastCreatedSubscription = (state) => {
  const subscriptionsResponse = getApiRequest(state, 'getAllSubscriptions');
  const allSubscriptions = subscriptionsResponse.get('data', Immutable.List());
  const filteredSubscriptions =
    allSubscriptions.size > 0 && allSubscriptions.toJS().filter((item) => item.status !== subscriptionStatus.PENDING);

  let lastSubscription = filteredSubscriptions && filteredSubscriptions[0];
  if (lastSubscription && filteredSubscriptions.length > 1) {
    for (let i = 1; i < filteredSubscriptions.length; i++) {
      if (filteredSubscriptions[i].createdAtTs > lastSubscription.createdAtTs) {
        lastSubscription = filteredSubscriptions[i];
      }
    }
  }

  return lastSubscription;
};

export const getLastSubscriptionExpirationDate = (state) => {
  const lastSubscription = getLastCreatedSubscription(state);
  return lastSubscription?.isActiveUntil;
};

export const getAllSubscriptionsApiStatus = (state) => getApiRequestStatus(state, 'getAllSubscriptions');

export const getSubscriptionId = createSelector(getLastCreatedSubscription, (subscription) => subscription?.id);
export const getSubscriptionType = createSelector(getLastCreatedSubscription, (subscription) => subscription?.type);

export const getVoucherFromActiveSubscription = (state) => {
  const activeSubscription = getActiveSubscription(state);
  return activeSubscription.voucherCode || null;
};

/**
 *
 * @deprecated outdated logic
 * TODO Recheck
 */
export const hasCanceledSubscription = (state) => {
  const activeSubscription = getLastCreatedSubscription(state);
  //User canceled the current subscription if does not have nextBillingPlan and active subscription has isActiveUntil field set or current subscription is in "Paused" status.

  return (
    !hasNextBillingPlan(state) &&
    activeSubscription &&
    (activeSubscription.isActiveUntil !== null || activeSubscription.status === subscriptionStatus.PAUSED)
  );
};

export const getIsSubscriptionPermanentlyCanceled = createSelector(getLastCreatedSubscription, (subscription) => {
  return !!(
    subscription &&
    subscription.status === subscriptionStatus.DELETED &&
    subscription.isActiveUntil &&
    dayjs().isBefore(subscription.isActiveUntil * 1000)
  );
});

export const hasUpgradedBefore = (state) => {
  const subscriptionsResponse = getApiRequest(state, 'getAllSubscriptions');
  const allSubscriptions = subscriptionsResponse.get('data', Immutable.List());
  return allSubscriptions.size > 0;
};

export const hasHigherPremiumPackageToUpgrade = (state) => {
  const platformName = getWebsite(state)['platform'];

  if (platformName === platform.WIX) {
    const featurePermissions = getCoreApiRequest(state, 'getFeaturePermissions');

    return featurePermissions && featurePermissions.wixPermissionPack < WixPermissionPack.PRO;
  } else {
    const lastCreatedSubscription = getLastCreatedSubscription(state);
    const paymentPlans = getPaymentPlans(state);
    if (lastCreatedSubscription && paymentPlans) {
      const activePlanIndex = getPlanIndex(state);

      return activePlanIndex < paymentPlans[lastCreatedSubscription.planType].length - 2;
    }

    return true;
  }
};

export const hasTrackingCodeError = createSelector(getWebsite, (website) => {
  const hasTrackingCodeInstalled = website && website.frontendFlags && website.frontendFlags.trackingCodeInstalled;
  const hadFirstVisit = website.hadFirstVisit;

  if (typeof hasTrackingCodeInstalled === 'boolean') {
    return !hasTrackingCodeInstalled;
  } else {
    return !hadFirstVisit;
  }
});

export const isWixClaimed = (state) => {
  const isWixWebsite = getWebsite(state).platform === platform.WIX;
  const isStandaloneApp = Config.appType === 'standalone';
  return isWixWebsite && isStandaloneApp;
};

export const getPaddleCheckoutEvents = (state) => {
  return state.getIn(['app', 'paddleCheckoutEvents']);
};

export const getOnboardingStep = (state) => state.getIn(['app', 'onboardingStep']);
export const getWebsiteCreationStep = (state) => state.getIn(['app', 'websiteCreationStep']);
export const getUpgradePlanStep = (state) => state.getIn(['app', 'upgradePlanStep']);
